import {
  PRESET_CATEGORIES,
  type PresetCategory,
  type PresetListing,
} from "@pax/api-contract/presets/v1alpha1";

/**
 * The orders the catalog can be read in. The store's shelves and the search
 * wall's filter are the same ones, so a shelf is a way into the wall already
 * arranged the way the shelf was.
 */
export const CATALOG_SORTS = [
  { id: "played", name: "Most played" },
  { id: "added", name: "Recently added" },
] as const;

export type CatalogSort = (typeof CATALOG_SORTS)[number]["id"];

export const DEFAULT_SORT: CatalogSort = "played";

export function catalogSortName(sort: CatalogSort): string {
  return CATALOG_SORTS.find(option => option.id === sort)?.name ?? "";
}

export function normalizeCatalogSort(value: unknown): CatalogSort | undefined {
  return CATALOG_SORTS.some(option => option.id === value) ? (value as CatalogSort) : undefined;
}

export function sortCatalog(listings: PresetListing[], sort: CatalogSort): PresetListing[] {
  switch (sort) {
    // The catalog is paged oldest first, so the newest of what has loaded is
    // at the end of it.
    case "added":
      return [...listings].reverse();
    default:
      return [...listings].sort((a, b) => b.gamesStarted - a.gamesStarted);
  }
}

/** Every category a scenario is listed under, in the order a filter lists them. */
export const CATEGORIES = Object.keys(PRESET_CATEGORIES) as PresetCategory[];

/** Narrowing by category, where none chosen means every one. */
export function filterByCategory(
  listings: PresetListing[],
  categories: PresetCategory[],
): PresetListing[] {
  if (categories.length === 0) return listings;
  return listings.filter(listing => categories.includes(listing.category));
}

/** Categories travel in a URL as a lowercase list: `historical,science-fiction`. */
function categoryParameter(category: PresetCategory): string {
  return category.toLowerCase().replaceAll(" ", "-");
}

export function categoriesToParameter(categories: PresetCategory[]): string | undefined {
  return categories.length > 0 ? categories.map(categoryParameter).join(",") : undefined;
}

/** Whatever was in the URL, as categories, in the order a filter lists them. */
export function normalizeCategories(value: unknown): PresetCategory[] {
  if (typeof value !== "string") return [];
  const chosen = new Set(value.split(","));
  return CATEGORIES.filter(category => chosen.has(categoryParameter(category)));
}
